// Stats helpers — derive per-chapter / per-topic / per-cognitive-level metrics.
//
// Decision (Session 19L): pure functions over (pkg, progress); no React, no IDB.
// Caller composes into widgets.

import type {
  CourseProgress,
  IngestPackage,
  TopicNode,
  CognitiveLevel,
  SubjectiveAttemptEntry,
} from '@examforge/shared/schemas';
import {
  isUnresolved,
  subjectiveNeedsRevisit,
  subjectiveLatestAvg,
  aggregateWrongAnswerStats,
} from '@examforge/shared/schemas';
import { getDueCards } from './srs.js';

export interface ChapterStats {
  /** caseId — the chapter identifier in v1 + v2. */
  chapterId: string;
  chapterName: string;
  /** Color hex from case definition; useful for accent rendering. */
  color: string;
  /** Counts. */
  totalFlashcards: number;
  dueFlashcards: number;
  retiredFlashcards: number;     // box 5
  totalMcqs: number;
  unresolvedWrong: number;
  bookmarks: number;
  /** Mastery 0..100; null when no data yet. Mirrors v1 formula loosely. */
  mastery: number | null;
  // Session 19Y · drill-down breakdown — counts per component within chapter.
  totalTfs: number;
  totalEssays: number;
  essayDrafted: number;
  totalMindmaps: number;
  totalGlossary: number;
  totalProofDrills: number;
  totalLectureNotes: number;
  // Session 19AO: new component counts per chapter
  totalFreeResponses: number;
  totalReadingPassages: number;
  /** 19AO: average self-score across this chapter's subjective attempts (NaN
   *  when no attempts). */
  subjectiveAvgScore: number;
  /** 19AO: subjective items below revisit threshold. */
  subjectiveNeedsRevisit: number;
}

export interface TopicStats {
  topicId: string;
  topicName: string;
  parentChapterId: string;
  totalItems: number;
  unresolvedWrong: number;
  bookmarks: number;
  dueFlashcards: number;
  /* Session 19AV: per-component counts for topic-card drill-down (mirror of
     ChapterStats fields used by BREAKDOWN_ROWS). Keeps TopicCard's expand
     UI 1:1 parallel to ChapterCard's. */
  totalFlashcards: number;
  totalMcqs: number;
  totalTfs: number;
  totalEssays: number;
  totalProofDrills: number;
  totalFreeResponses: number;
  totalGlossary: number;
  totalLectureNotes: number;
  totalMindmaps: number;
  totalReadingPassages: number;
}

export interface CognitiveDistribution {
  level: CognitiveLevel | 'unlabeled';
  count: number;
}

const COG_ORDER: readonly CognitiveLevel[] = ['recall', 'understand', 'apply', 'analyze', 'evaluate'];

export function chapterStats(pkg: IngestPackage, progress: CourseProgress): ChapterStats[] {
  return pkg.cases.map((c) => {
    const fcInChapter = pkg.flashcards.filter((f) => f.caseId === c.id);
    const mcqsInChapter = pkg.mcqs.filter((m) => m.caseId === c.id);
    const tfsInChapter = (pkg.trueFalse ?? []).filter((tf) => tf.caseId === c.id);
    const essaysInChapter = (pkg.essays ?? []).filter((e) => e.caseId === c.id);
    const mindmapsInChapter = (pkg.mindmaps ?? []).filter((m) => m.caseId === c.id);
    const glossaryInChapter = (pkg.glossary ?? []).filter((g) => g.caseIds.includes(c.id));
    const proofDrillsInChapter = (pkg.proofDrills ?? []).filter((p) => p.caseId === c.id);
    // Session 19AE: LectureNote.caseId is single (one chapter per note).
    const lectureNotesInChapter = (pkg.lectureNotes ?? []).filter((l) => l.caseId === c.id);
    // Session 19AO: free_response + reading_passage per chapter
    const freeResponsesInChapter = (pkg.freeResponses ?? []).filter((fr) => fr.caseId === c.id);
    const readingPassagesInChapter = (pkg.readingPassages ?? []).filter((rp) => rp.caseId === c.id);

    // Session 19AO: subjective-attempt aggregation per chapter (essay /
    // free_response / proof_drill with self-scores).
    const subjAttempts = Object.entries(progress.subjectiveAttempts ?? {})
      .filter(([, attempt]) => attempt.caseId === c.id);
    let subjAvgSum = 0;
    let subjAvgCount = 0;
    let subjNeedsRevisit = 0;
    for (const [, attempt] of subjAttempts) {
      const latestAvg = subjectiveLatestAvg(attempt);
      if (!Number.isNaN(latestAvg)) {
        subjAvgSum += latestAvg;
        subjAvgCount++;
        if (subjectiveNeedsRevisit(attempt)) subjNeedsRevisit++;
      }
    }
    const subjectiveAvgScore = subjAvgCount > 0 ? subjAvgSum / subjAvgCount : NaN;

    const dueFlashcards = getDueCards(fcInChapter, progress).length;
    const retiredFlashcards = fcInChapter.filter(
      (f) => (progress.srs[f.id]?.box ?? 1) === 5,
    ).length;

    const wrongInChapter = Object.entries(progress.wrongAnswers)
      .filter(([, e]) => e.caseId === c.id);
    const unresolvedWrong = wrongInChapter.filter(([, e]) => isUnresolved(e)).length;

    const bookmarksInChapter = Object.entries(progress.bookmarks)
      .filter(([, b]) => b.caseId === c.id).length;

    // Essay drafted count — any essay whose draft has non-empty content.
    const essayDrafted = essaysInChapter.filter((e) => {
      const d = progress.essayDrafts[e.id];
      return Boolean(d && d.content.trim().length > 0);
    }).length;

    // Mastery: simplified. Avg of (1) flashcard SRS box→score mapping (boxes 1..5
    // map 0/0.3/0.6/0.85/1.0) (2) wrong-answer ledger penalty.
    const fcScore = fcInChapter.length > 0
      ? fcInChapter.reduce((acc, fc) => {
          const box = progress.srs[fc.id]?.box ?? 1;
          return acc + boxScore(box);
        }, 0) / fcInChapter.length
      : null;
    const totalQ = mcqsInChapter.length;
    const wrongPenalty = totalQ > 0 ? Math.min(0.25, unresolvedWrong / totalQ) : 0;
    const mastery = fcScore !== null
      ? Math.round(fcScore * (1 - wrongPenalty) * 100)
      : null;

    return {
      chapterId: c.id,
      chapterName: c.title,
      color: c.color,
      totalFlashcards: fcInChapter.length,
      dueFlashcards,
      retiredFlashcards,
      totalMcqs: mcqsInChapter.length,
      unresolvedWrong,
      bookmarks: bookmarksInChapter,
      mastery,
      totalTfs: tfsInChapter.length,
      totalEssays: essaysInChapter.length,
      essayDrafted,
      totalMindmaps: mindmapsInChapter.length,
      totalGlossary: glossaryInChapter.length,
      totalProofDrills: proofDrillsInChapter.length,
      totalLectureNotes: lectureNotesInChapter.length,
      totalFreeResponses: freeResponsesInChapter.length,
      totalReadingPassages: readingPassagesInChapter.length,
      subjectiveAvgScore,
      subjectiveNeedsRevisit: subjNeedsRevisit,
    };
  });
}

export function topicStats(pkg: IngestPackage, progress: CourseProgress): TopicStats[] {
  const hierarchy = pkg.topicHierarchy ?? [];
  const topics = hierarchy.filter((n: TopicNode) => n.level === 'topic');
  return topics.map((topic) => {
    // Session 19AV: tally every item kind that carries a topicId. Parallels
    // chapterStats() so TopicCard can render the same breakdown grid.
    const fcInTopic = pkg.flashcards.filter((f) => f.topicId === topic.id);
    const mcqInTopic = pkg.mcqs.filter((m) => m.topicId === topic.id);
    const tfsInTopic = (pkg.trueFalse ?? []).filter((tf) => tf.topicId === topic.id);
    const essaysInTopic = (pkg.essays ?? []).filter((e) => e.topicId === topic.id);
    const proofsInTopic = (pkg.proofDrills ?? []).filter((p) => p.topicId === topic.id);
    const freeResponsesInTopic = (pkg.freeResponses ?? []).filter((fr) => fr.topicId === topic.id);
    const glossaryInTopic = (pkg.glossary ?? []).filter((g) => g.topicIds?.includes(topic.id) ?? false);
    const lectureNotesInTopic = (pkg.lectureNotes ?? []).filter((l) => l.topicId === topic.id);
    // ReadingPassage doesn't carry topicId at the passage level (its inner
    // mcq/tf items do via questionRefs cross-link). Reading is conceptually
    // chapter-scoped, so we don't surface a per-topic count here. Revisit if
    // reading usage grows enough to warrant per-topic dispatch.
    // Mindmaps are per-chapter not per-topic, so attribute via parentChapter:
    // a topic's "available mindmaps" = mindmaps in its parent chapter.
    const mindmapsInTopic = (pkg.mindmaps ?? []).filter((m) => m.caseId === topic.parentId);

    const totalItems =
      fcInTopic.length + mcqInTopic.length + tfsInTopic.length +
      essaysInTopic.length + proofsInTopic.length + freeResponsesInTopic.length;
    const dueFlashcards = getDueCards(fcInTopic, progress).length;
    const wrongInTopic = Object.entries(progress.wrongAnswers).filter(([id]) => {
      const mcq = mcqInTopic.find((m) => m.id === id);
      const tf = tfsInTopic.find((t) => t.id === id);
      return Boolean(mcq || tf);
    });
    const unresolvedWrong = wrongInTopic.filter(([, e]) => isUnresolved(e)).length;
    const bookmarksInTopic = Object.entries(progress.bookmarks).filter(([id]) => {
      return fcInTopic.find((f) => f.id === id) ||
        mcqInTopic.find((m) => m.id === id) ||
        tfsInTopic.find((t) => t.id === id);
    }).length;
    return {
      topicId: topic.id,
      topicName: topic.name,
      parentChapterId: topic.parentId ?? '',
      totalItems,
      unresolvedWrong,
      bookmarks: bookmarksInTopic,
      dueFlashcards,
      totalFlashcards: fcInTopic.length,
      totalMcqs: mcqInTopic.length,
      totalTfs: tfsInTopic.length,
      totalEssays: essaysInTopic.length,
      totalProofDrills: proofsInTopic.length,
      totalFreeResponses: freeResponsesInTopic.length,
      totalGlossary: glossaryInTopic.length,
      totalLectureNotes: lectureNotesInTopic.length,
      totalMindmaps: mindmapsInTopic.length,
      totalReadingPassages: 0,
    };
  });
}

/** Distribution of cognitive levels across ALL items carrying cognitiveLevel
 *  (mcq / flashcard / true_false / proof_drill / lecture_notes / free_response).
 *  Items lacking the field count as 'unlabeled'.
 *
 *  Session 19AO (陛下: "bloom 哪里都要对应上"): now spans all item types with
 *  the cognitiveLevel field, not just MCQ. lecture_notes carries
 *  cognitiveLevels[] (array, multi-tier per note); we count each tier mention. */
export function cognitiveDistribution(pkg: IngestPackage): CognitiveDistribution[] {
  const buckets = new Map<CognitiveLevel | 'unlabeled', number>();
  for (const lv of COG_ORDER) buckets.set(lv, 0);
  buckets.set('unlabeled', 0);
  const bump = (k: CognitiveLevel | 'unlabeled') => {
    buckets.set(k, (buckets.get(k) ?? 0) + 1);
  };
  for (const m of pkg.mcqs) bump((m.cognitiveLevel ?? 'unlabeled') as CognitiveLevel | 'unlabeled');
  for (const f of pkg.flashcards) bump((f.cognitiveLevel ?? 'unlabeled') as CognitiveLevel | 'unlabeled');
  for (const tf of pkg.trueFalse ?? []) bump((tf.cognitiveLevel ?? 'unlabeled') as CognitiveLevel | 'unlabeled');
  for (const pd of pkg.proofDrills ?? []) bump((pd.cognitiveLevel ?? 'unlabeled') as CognitiveLevel | 'unlabeled');
  for (const fr of pkg.freeResponses ?? []) bump((fr.cognitiveLevel ?? 'unlabeled') as CognitiveLevel | 'unlabeled');
  for (const ln of pkg.lectureNotes ?? []) {
    if (ln.cognitiveLevels && ln.cognitiveLevels.length > 0) {
      // lecture_notes carries an array of tiers; count each.
      for (const lv of ln.cognitiveLevels) bump(lv as CognitiveLevel);
    } else {
      bump('unlabeled');
    }
  }
  return [...COG_ORDER, 'unlabeled' as const].map((lv) => ({
    level: lv,
    count: buckets.get(lv) ?? 0,
  }));
}

/** Top N weakest chapters by mastery; undefined mastery sorts last. */
export function weakestChapters(stats: ChapterStats[], n = 3): ChapterStats[] {
  return [...stats]
    .filter((s) => s.mastery !== null && s.mastery < 80)
    .sort((a, b) => (a.mastery ?? 0) - (b.mastery ?? 0))
    .slice(0, n);
}

/** Top N weakest topics by unresolved wrong count. */
export function weakestTopics(stats: TopicStats[], n = 3): TopicStats[] {
  return [...stats]
    .filter((s) => s.unresolvedWrong > 0)
    .sort((a, b) => b.unresolvedWrong - a.unresolvedWrong)
    .slice(0, n);
}

// ── Wrong answer stats (Session 19V §E-Q7) ──────────────────────────────

export interface WrongAnswerStats {
  total: number;
  unresolved: number;
  resolved: number;
  byCase: { caseId: string; caseName: string; count: number; unresolved: number }[];
  byCategory: { category: string; count: number; unresolved: number }[];
  byCognitive: CognitiveDistribution[];
  /** Last 14 days, day-bucketed wrong-event counts (0 = today, 13 = oldest). */
  recentTrend: { dayOffset: number; wrongEvents: number }[];
}

export function wrongAnswerStats(pkg: IngestPackage, progress: CourseProgress): WrongAnswerStats {
  const mcqById = new Map(pkg.mcqs.map((m) => [m.id, m]));
  const tfById = new Map((pkg.trueFalse ?? []).map((tf) => [tf.id, tf]));
  const caseById = new Map(pkg.cases.map((c) => [c.id, c]));

  // Delegate the per-item aggregation to the shared core
  // (DEBT-19BO+-STATS-MIRROR-PARITY-GATE — single source of truth, shared with
  // ingest/aggregate-stats.ts). Web passes uncapped: wrong-answers-view.tsx
  // computes a global bar-chart max across the full byCase array.
  return aggregateWrongAnswerStats(
    Object.entries(progress.wrongAnswers),
    { mcqById, tfById, caseById },
    Date.now(),
  );
}

// ── Chapter × cognitive-level matrix (19BO+ B5.3 dashboard heatmap) ──────

/** One cell of the chapter × cognitive-level heatmap.
 *
 *  `unresolved` is the heat value (wrongCount > correctCount). `total` counts
 *  every wrong-answer ledger row for that cell (resolved + unresolved) — useful
 *  for the drill-down tooltip ("曾错过 N 次，其中 M 次仍未解决"). */
export interface ChapterCognitiveCell {
  chapterId: string;
  chapterName: string;
  level: CognitiveLevel | 'unlabeled';
  /** Wrong-answer rows in this cell still unresolved (wrongCount > correctCount). */
  unresolved: number;
  /** Total wrong-answer rows that ever landed in this cell. */
  total: number;
}

/** Per-chapter × per-cognitive-level aggregation of the wrong-answer ledger.
 *
 *  Only mcqs + true_false feed the matrix — they are the "graded wrong" item
 *  types that carry a `cognitiveLevel`. Flashcards route through SRS, and
 *  essays / free_responses through subjective scoring; neither enters
 *  `progress.wrongAnswers`, so neither enters the matrix.
 *
 *  Row order = `pkg.cases` order; column order = recall, understand, apply,
 *  analyze, evaluate, unlabeled (matches COG_ORDER + the 'unlabeled' tail used
 *  by `cognitiveDistribution`). Empty progress still yields a full
 *  `cases.length × 6` matrix of zero cells (so the heatmap can render a stable
 *  grid even before the user has answered anything).
 *
 *  B5.3 fills an existing data-layer gap: `chapterStats` has per-chapter
 *  mastery/unresolved but no cognitive split; `cognitiveDistribution` is
 *  global; `wrongAnswerStats` has byCase + byCognitive but never joins them.
 *  This function is that join — the heat value per cell. */
export function chapterCognitiveMatrix(
  pkg: IngestPackage,
  progress: CourseProgress,
): ChapterCognitiveCell[] {
  // Index mcq + tf by id so we can resolve (caseId, cognitiveLevel) per ledger row.
  const mcqById = new Map(pkg.mcqs.map((m) => [m.id, m]));
  const tfById = new Map((pkg.trueFalse ?? []).map((tf) => [tf.id, tf]));
  const caseNameById = new Map(pkg.cases.map((c) => [c.id, c.title]));

  // agg[chapterId][levelKey] = { unresolved, total }
  const agg = new Map<string, Map<CognitiveLevel | 'unlabeled', { unresolved: number; total: number }>>();

  for (const [id, entry] of Object.entries(progress.wrongAnswers)) {
    const q = mcqById.get(id) ?? tfById.get(id);
    if (!q) continue; // ledger row points at a non-mcq/tf id (or a deleted item)
    // Prefer the ledger's caseId (user-facing chapter at answer time); fall back
    // to the question's own caseId. The ledger field is nullable in schema, so
    // coalesce; if both are missing, skip (can't attribute to a chapter).
    const cId = entry.caseId ?? q.caseId;
    if (!cId) continue;
    const levelKey = (q.cognitiveLevel ?? 'unlabeled') as CognitiveLevel | 'unlabeled';
    let chapterMap = agg.get(cId);
    if (!chapterMap) {
      chapterMap = new Map();
      agg.set(cId, chapterMap);
    }
    let cell = chapterMap.get(levelKey);
    if (!cell) {
      cell = { unresolved: 0, total: 0 };
      chapterMap.set(levelKey, cell);
    }
    cell.total++;
    if (isUnresolved(entry)) cell.unresolved++;
  }

  // Emit a full cases × levels grid (stable order, zero-filled).
  const levels: ReadonlyArray<CognitiveLevel | 'unlabeled'> = [...COG_ORDER, 'unlabeled'];
  const out: ChapterCognitiveCell[] = [];
  for (const c of pkg.cases) {
    const chapterMap = agg.get(c.id);
    for (const level of levels) {
      const cell = chapterMap?.get(level) ?? { unresolved: 0, total: 0 };
      out.push({
        chapterId: c.id,
        chapterName: caseNameById.get(c.id) ?? c.title,
        level,
        unresolved: cell.unresolved,
        total: cell.total,
      });
    }
  }
  return out;
}

function boxScore(box: number): number {
  switch (box) {
    case 1: return 0;
    case 2: return 0.3;
    case 3: return 0.6;
    case 4: return 0.85;
    case 5: return 1.0;
    default: return 0;
  }
}

// ── Glossary stats (Session 19X · v1↔v2 parity) ─────────────────────────
//
// Pure derivation over IngestPackage. Used by GlossaryView for the alpha
// index + per-case filter dropdown; future export-to-CSV / cross-glossary
// search reuse this without touching the view.

export interface GlossaryAlphaBucket {
  /** Uppercase letter (or '#' for non-alpha leading char). */
  letter: string;
  count: number;
}

export interface GlossaryStats {
  /** Total terms after no filter. */
  total: number;
  /** Alphabetical buckets present in pkg, sorted A → Z, '#' last. */
  alphaBuckets: GlossaryAlphaBucket[];
  /** Per-case bucket — count of terms referencing each case (caseIds[]). */
  byCase: { caseId: string; caseName: string; count: number }[];
  /** Untagged-by-case terms count (caseIds is empty). */
  unscopedCount: number;
}

export function glossaryStats(pkg: IngestPackage): GlossaryStats {
  const items = pkg.glossary ?? [];
  const caseById = new Map(pkg.cases.map((c) => [c.id, c]));

  const alpha = new Map<string, number>();
  const byCase = new Map<string, number>();
  let unscopedCount = 0;

  for (const g of items) {
    alpha.set(letterKey(g.term), (alpha.get(letterKey(g.term)) ?? 0) + 1);
    if (g.caseIds.length === 0) {
      unscopedCount += 1;
    } else {
      for (const cid of g.caseIds) {
        byCase.set(cid, (byCase.get(cid) ?? 0) + 1);
      }
    }
  }

  const alphaBuckets = [...alpha.entries()]
    .map(([letter, count]) => ({ letter, count }))
    .sort((a, b) => {
      // '#' (non-alpha leading) sorts last
      if (a.letter === '#') return 1;
      if (b.letter === '#') return -1;
      return a.letter.localeCompare(b.letter);
    });

  const byCaseList = [...byCase.entries()]
    .map(([caseId, count]) => ({
      caseId,
      caseName: caseById.get(caseId)?.title ?? caseId,
      count,
    }))
    .sort((a, b) => b.count - a.count || a.caseName.localeCompare(b.caseName));

  return {
    total: items.length,
    alphaBuckets,
    byCase: byCaseList,
    unscopedCount,
  };
}

function letterKey(term: string): string {
  const c = term.trim()[0];
  if (!c) return '#';
  const upper = c.toUpperCase();
  return /^[A-Z]$/.test(upper) ? upper : '#';
}

// ── Session 19AO: subjective-attempt stats (parallel to wrongAnswerStats but
// for free_response / essay / proof_drill items). Per陛下: "在错题本中应该
// 有新的追踪形式和统计形式，和 mcq, tf 这种客观题分开"。

export interface SubjectiveAttemptStats {
  /** Total subjective items the user has attempted at least once. */
  total: number;
  /** Items below the revisit threshold (avg latest score < 2.0). */
  needsRevisitCount: number;
  /** Per-type breakdown (essay / free_response / proof_drill). */
  byType: { type: SubjectiveAttemptEntry['type']; count: number; needsRevisit: number; avgScore: number }[];
  /** Per-chapter breakdown — count + avg of latest scores within the chapter. */
  byChapter: { caseId: string; caseName: string; count: number; needsRevisit: number; avgScore: number }[];
  /** Items sorted by avg ascending (worst first) — top N for "weakest" widget. */
  weakestItems: { itemId: string; type: SubjectiveAttemptEntry['type']; caseId: string | null; avgScore: number }[];
}

export function subjectiveAttemptStats(
  pkg: IngestPackage,
  progress: CourseProgress,
): SubjectiveAttemptStats {
  const attempts = progress.subjectiveAttempts ?? {};
  const caseById = new Map(pkg.cases.map((c) => [c.id, c]));

  let total = 0;
  let needsRevisitCount = 0;

  const byTypeMap = new Map<SubjectiveAttemptEntry['type'], { count: number; needsRevisit: number; sumScore: number; sampleCount: number }>();
  for (const t of ['essay', 'free_response', 'proof_drill'] as const) {
    byTypeMap.set(t, { count: 0, needsRevisit: 0, sumScore: 0, sampleCount: 0 });
  }
  const byChapterMap = new Map<string, { count: number; needsRevisit: number; sumScore: number; sampleCount: number }>();
  const weakest: { itemId: string; type: SubjectiveAttemptEntry['type']; caseId: string | null; avgScore: number }[] = [];

  for (const [itemId, entry] of Object.entries(attempts)) {
    total++;
    const needs = subjectiveNeedsRevisit(entry);
    if (needs) needsRevisitCount++;
    const avg = subjectiveLatestAvg(entry);

    const tStats = byTypeMap.get(entry.type)!;
    tStats.count++;
    if (needs) tStats.needsRevisit++;
    if (!Number.isNaN(avg)) {
      tStats.sumScore += avg;
      tStats.sampleCount++;
    }

    if (entry.caseId !== null) {
      let cStats = byChapterMap.get(entry.caseId);
      if (!cStats) {
        cStats = { count: 0, needsRevisit: 0, sumScore: 0, sampleCount: 0 };
        byChapterMap.set(entry.caseId, cStats);
      }
      cStats.count++;
      if (needs) cStats.needsRevisit++;
      if (!Number.isNaN(avg)) {
        cStats.sumScore += avg;
        cStats.sampleCount++;
      }
    }

    if (!Number.isNaN(avg)) {
      weakest.push({ itemId, type: entry.type, caseId: entry.caseId, avgScore: avg });
    }
  }

  weakest.sort((a, b) => a.avgScore - b.avgScore);

  return {
    total,
    needsRevisitCount,
    byType: [...byTypeMap.entries()].map(([type, s]) => ({
      type,
      count: s.count,
      needsRevisit: s.needsRevisit,
      avgScore: s.sampleCount > 0 ? s.sumScore / s.sampleCount : NaN,
    })),
    byChapter: [...byChapterMap.entries()]
      .map(([caseId, s]) => ({
        caseId,
        caseName: caseById.get(caseId)?.title ?? caseId,
        count: s.count,
        needsRevisit: s.needsRevisit,
        avgScore: s.sampleCount > 0 ? s.sumScore / s.sampleCount : NaN,
      }))
      .sort((a, b) => b.count - a.count),
    weakestItems: weakest.slice(0, 5),
  };
}
